import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchClusters } from '../lib/api.js';

const REFETCH_MS = 30_000;
const DEBOUNCE_MS = 400;

/**
 * Debounce pra não martelar a API durante pan/zoom (cada onMove dispara
 * setViewport e a query teria que recalcular). 400ms é suficiente pra deixar
 * o usuário "pousar" o mapa antes do refetch.
 */
function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * Hook de clustering server-side. Refetch automático ao pan/zoom (debounced).
 *
 * `enabled` controla se a query roda — o MapCanvas usa pra desativar quando
 * `matched < 200` ou zoom alto, evitando custo zero da requisição.
 *
 *   { clusters, source, isLoading, isFetching, error }
 */
export function useClusters({
  bbox,
  zoom,
  status = 'bad',
  olt,
  search,
  minAgingMin,
  maxAgingMin,
  enabled = true,
} = {}) {
  const debouncedBbox = useDebouncedValue(bbox, DEBOUNCE_MS);
  const debouncedZoom = useDebouncedValue(zoom, DEBOUNCE_MS);

  const q = useQuery({
    queryKey: [
      'v2',
      'clusters',
      {
        bbox: debouncedBbox,
        zoom: debouncedZoom,
        status,
        olt,
        search,
        minAgingMin,
        maxAgingMin,
      },
    ],
    queryFn: () =>
      fetchClusters({
        bbox: debouncedBbox,
        zoom: debouncedZoom,
        status,
        olt,
        search,
        minAgingMin,
        maxAgingMin,
      }),
    enabled: enabled && Boolean(debouncedBbox) && Number.isFinite(debouncedZoom),
    refetchInterval: REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });

  const raw = q.data?.data;
  const clusters = Array.isArray(raw) ? raw : [];

  return {
    clusters,
    source: q.data?.source,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: q.error,
  };
}
